import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  makeStyles,
  Box,
  CircularProgress,
  Grid,
  Divider,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Avatar,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormHelperText,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ContentCopyIcon from "@material-ui/icons/FileCopy";
import LockIcon from "@material-ui/icons/Lock";
import EventIcon from "@material-ui/icons/Event";
import PlaceIcon from "@material-ui/icons/Place";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Link, useLocation } from "react-router-dom";
import AiSensyLogo from "../../static/AiSensyLogo.svg";
import { INTEGRATION_SERVER_URL } from "../../config/config";
import axios from "axios";
import { countries } from "./countryData";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    padding: 0,
    margin: 0,
    backgroundImage: `
      linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(250, 250, 252, 0.95)), 
      url('https://images.unsplash.com/photo-1546412414-e1885e51cec5?q=80&w=1974')
    `,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
    position: "relative",
    color: "#111827",
    [theme.breakpoints.down("xs")]: {
      backgroundAttachment: "scroll",
    },
  },
  container: {
    position: "relative",
    zIndex: 1,
    padding: theme.spacing(3, 2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2, 1),
    },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  contentWrapper: {
    width: "100%",
    maxWidth: 700,
  },
  header: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },
  logo: {
    height: 36,
    marginRight: theme.spacing(1),
  },
  evolveTitle: {
    fontSize: "clamp(2rem, 6vw, 3.8rem)",
    fontWeight: 800,
    lineHeight: 1,
    marginBottom: theme.spacing(0.5),
    backgroundImage: "linear-gradient(to right, #111827, #04cf66)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    textTransform: "uppercase",
    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    textAlign: "center",
    letterSpacing: "-0.01em",
    [theme.breakpoints.down("xs")]: {
      fontSize: "2rem",
    },
  },
  subtitle: {
    fontSize: "clamp(0.7rem, 1.5vw, 0.75rem)",
    letterSpacing: "0.1em",
    color: "#6B7280",
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
    textAlign: "center",
    fontWeight: 600,
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.65rem",
      marginBottom: theme.spacing(0.5),
    },
  },
  thankYouSection: {
    textAlign: "center",
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2, 3),
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.03)",
    position: "relative",
    overflow: "hidden",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "4px",
      background: "linear-gradient(to right, #04cf66, #28E0B9)",
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(2),
    },
  },
  successIcon: {
    width: 60,
    height: 60,
    backgroundColor: "rgba(4, 207, 102, 0.08)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    marginBottom: theme.spacing(2),
    "& svg": {
      color: "#04cf66",
      fontSize: 36,
    },
  },
  congratsText: {
    fontSize: "clamp(1.4rem, 4vw, 1.7rem)",
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    color: "#111827",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.3rem",
      marginBottom: theme.spacing(0.5),
    },
  },
  highlightText: {
    color: "#04cf66",
    fontWeight: 700,
  },
  orderSummarySection: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(2),
    },
  },
  sectionTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
    color: "#111827",
    position: "relative",
    paddingLeft: theme.spacing(1),
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: "4px",
      height: "70%",
      background: "linear-gradient(to bottom, #04cf66, #28E0B9)",
      borderRadius: "2px",
    },
  },
  ticketCard: {
    backgroundColor: "white",
    borderRadius: "10px",
    overflow: "hidden",
    position: "relative",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.04)",
  },
  ticketHeader: {
    padding: theme.spacing(1, 1.5),
    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
    backgroundColor: "rgba(4, 207, 102, 0.03)",
    display: "flex",
    alignItems: "center",
  },
  ticketHeaderIcon: {
    marginRight: theme.spacing(1),
    color: "#04cf66",
    fontSize: "1.2rem",
  },
  ticketTitle: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#374151",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8rem",
    },
  },
  ticketContent: {
    padding: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },
  ticketDetail: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.3),
    padding: theme.spacing(0.2, 0),
    fontSize: "0.85rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8rem",
      marginBottom: theme.spacing(0.2),
    },
  },
  ticketType: {
    color: "#4B5563",
    fontWeight: 500,
  },
  ticketPrice: {
    color: "#04cf66",
    fontWeight: 600,
  },
  ticketTotal: {
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    backgroundColor: "rgba(4, 207, 102, 0.04)",
    borderRadius: "6px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  totalText: {
    color: "#374151",
    fontWeight: 600,
    fontSize: "0.85rem",
  },
  totalAmount: {
    color: "#04cf66",
    fontWeight: 700,
    fontSize: "1.1rem",
  },
  eventDetails: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1),
    backgroundColor: "rgba(0, 0, 0, 0.02)",
    borderRadius: "6px",
  },
  eventDetailItem: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.85rem",
    color: "#4B5563",
  },
  eventDetailIcon: {
    color: "#6B7280",
    fontSize: "1rem",
    marginRight: theme.spacing(1),
  },
  formSection: {
    marginTop: theme.spacing(2),
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      marginTop: theme.spacing(1.5),
    },
  },
  formTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: "#111827",
    position: "relative",
    paddingLeft: theme.spacing(1),
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: "4px",
      height: "70%",
      background: "linear-gradient(to bottom, #04cf66, #28E0B9)",
      borderRadius: "2px",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "1rem",
      marginBottom: theme.spacing(1.5),
    },
  },
  attendeeForm: {
    backgroundColor: "white",
    borderRadius: "10px",
    marginBottom: theme.spacing(2),
    overflow: "hidden",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.04)",
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(1.5),
    },
  },
  formHeader: {
    backgroundColor: "rgba(4, 207, 102, 0.04)",
    padding: theme.spacing(1.2, 2),
    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.8, 1.2),
    },
  },
  formContent: {
    padding: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },
  formField: {
    marginBottom: theme.spacing(1.8),
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(1),
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "& fieldset": {
        borderColor: "rgba(0, 0, 0, 0.08)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(0, 0, 0, 0.15)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(0, 0, 0, 0.2)",
        borderWidth: "1px",
      },
    },
    "&.MuiTextField-root": {
      padding: "0px",
    },
    "& .MuiInputLabel-root": {
      color: "#6B7280",
      fontSize: "0.85rem",
      fontWeight: 500,
    },
    "& .MuiOutlinedInput-input": {
      color: "#1F2937",
      fontSize: "0.9rem",
    },
    "& .MuiSelect-root": {
      backgroundColor: "#f0f0f0",
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      color: "#6B7280",
      fontSize: "0.8rem",
    },
    "& .MuiFormHelperText-root": {
      color: "#EF4444",
      marginTop: 2,
      fontSize: "0.7rem",
    },
  },
  copyButton: {
    backgroundColor: "rgba(4, 207, 102, 0.06)",
    color: "#04cf66",
    border: "1px solid rgba(4, 207, 102, 0.15)",
    padding: theme.spacing(0.1, 0.8),
    fontSize: "0.65rem",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.1, 0.6),
      fontSize: "0.6rem",
    },
    "&:hover": {
      backgroundColor: "rgba(4, 207, 102, 0.1)",
    },
    textTransform: "none",
    minWidth: "auto",
    borderRadius: "4px",
    fontWeight: 500,
    height: "24px",
    [theme.breakpoints.down("xs")]: {
      height: "22px",
    },
  },
  copyIcon: {
    fontSize: "0.75rem",
    marginRight: theme.spacing(0.3),
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.7rem",
      marginRight: theme.spacing(0.2),
    },
  },
  submitButton: {
    background: "linear-gradient(to right, #04cf66, #28E0B9)",
    color: "white",
    padding: theme.spacing(1.2, 0),
    fontWeight: 600,
    fontSize: "0.95rem",
    marginTop: theme.spacing(1),
    "&:hover": {
      background: "linear-gradient(to right, #03b85a, #25C9A7)",
    },
    width: "100%",
    borderRadius: "8px",
    textTransform: "none",
    boxShadow: "0 4px 12px rgba(4, 207, 102, 0.15)",
    transition: "all 0.2s ease",
    "&:active": {
      transform: "scale(0.98)",
    },
  },
  divider: {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    margin: theme.spacing(0.7, 0),
  },
  formSubmitArea: {
    marginTop: theme.spacing(3),
    backgroundColor: "white",
    borderRadius: "12px",
    padding: theme.spacing(2),
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.04)",
    maxWidth: 700,
    margin: "0 auto",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5),
      marginTop: theme.spacing(2),
    },
  },
  phoneInput: {
    display: "flex",
  },
  countrySelect: {
    minWidth: 95,
    marginRight: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "& fieldset": {
        borderColor: "rgba(0, 0, 0, 0.08)",
      },
    },
    "& .MuiSelect-root": {
      color: "#1F2937",
      fontSize: "0.9rem",
      padding: "12px 32px 12px 12px",
    },
    "& .MuiInputLabel-root": {
      color: "#6B7280",
      fontSize: "0.85rem",
      fontWeight: 500,
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      fontSize: "0.8rem",
    },
  },
  successMessage: {
    backgroundColor: "rgba(4, 207, 102, 0.04)",
    color: "#04cf66",
    padding: theme.spacing(2),
    borderRadius: "10px",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    "& .MuiSvgIcon-root": {
      marginRight: theme.spacing(1),
    },
    border: "1px solid rgba(4, 207, 102, 0.1)",
  },
  bookAgainButton: {
    background: "linear-gradient(to right, #04cf66, #28E0B9)",
    color: "white",
    padding: theme.spacing(0.8, 3),
    borderRadius: "8px",
    fontWeight: 600,
    marginTop: theme.spacing(2),
    "&:hover": {
      background: "linear-gradient(to right, #03b85a, #25C9A7)",
    },
    textTransform: "none",
    fontSize: "0.9rem",
    boxShadow: "0 4px 12px rgba(4, 207, 102, 0.15)",
  },
  attendeeTitle: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#374151",
    display: "flex",
    alignItems: "center",
  },
  attendeeCount: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    borderRadius: "50%",
    backgroundColor: "#04cf66",
    color: "white",
    fontSize: "0.7rem",
    fontWeight: 700,
    marginRight: theme.spacing(1),
  },
  messageText: {
    fontSize: "clamp(0.85rem, 2vw, 0.95rem)",
    color: "#4B5563",
    lineHeight: 1.6,
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.85rem",
      lineHeight: 1.4,
    },
  },
  headerTitle: {
    fontSize: "1.1rem",
    color: "#111827",
    margin: theme.spacing(1, 0, 1.5),
    fontWeight: 700,
    position: "relative",
    paddingLeft: theme.spacing(1),
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: "4px",
      height: "70%",
      background: "linear-gradient(to bottom, #04cf66, #28E0B9)",
      borderRadius: "2px",
    },
  },
  secureNotice: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.75rem",
    color: "#6B7280",
    marginTop: theme.spacing(3),
    "& svg": {
      fontSize: "0.85rem",
      marginRight: theme.spacing(0.5),
    },
  },
  eventTimeLocation: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "center",
      gap: theme.spacing(0.8),
      marginBottom: theme.spacing(2),
    },
  },
  eventDetailPill: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.85rem",
    color: "#374151",
    padding: theme.spacing(0.7, 1.5),
    backgroundColor: "rgba(4, 207, 102, 0.08)",
    borderRadius: "20px",
    fontWeight: 500,
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    border: "1px solid rgba(4, 207, 102, 0.1)",
    "& svg": {
      marginRight: theme.spacing(0.7),
      fontSize: "1rem",
      color: "#04cf66",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.75rem",
      padding: theme.spacing(0.5, 1.2),
      width: "90%",
      justifyContent: "center",
    },
  },
  eventBadges: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.8),
    marginBottom: theme.spacing(2),
    justifyContent: "center",
    [theme.breakpoints.down("xs")]: {
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(1.5),
    },
  },
  dubaiChip: {
    backgroundColor: "#FFF8E1",
    color: "#FF9800",
    fontWeight: 500,
    fontSize: "0.7rem",
    border: "1px solid rgba(255, 152, 0, 0.2)",
    height: "24px",
  },
  whatsappChip: {
    backgroundColor: "#E8F5E9",
    color: "#25D366",
    fontWeight: 500,
    fontSize: "0.7rem",
    border: "1px solid rgba(37, 211, 102, 0.2)",
    height: "24px",
  },
  aiChip: {
    backgroundColor: "#E3F2FD",
    color: "#2196F3",
    fontWeight: 500,
    fontSize: "0.7rem",
    border: "1px solid rgba(33, 150, 243, 0.2)",
    height: "24px",
  },
  businessChip: {
    backgroundColor: "#FFF8E1",
    color: "#FF9800",
    fontWeight: 500,
    fontSize: "0.7rem",
    border: "1px solid rgba(255, 152, 0, 0.2)",
    height: "24px",
  },
  customIcon: {
    width: 16,
    height: 16,
    marginRight: theme.spacing(0.5),
  },
  summaryAccordion: {
    borderRadius: "10px",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.04)",
    overflow: "hidden",
    "&::before": {
      display: "none",
    },
    "& .MuiAccordionSummary-root": {
      padding: theme.spacing(0.5, 1),
      minHeight: "auto",
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(0.3, 0.8),
      },
    },
    "& .MuiAccordionSummary-content": {
      margin: "4px 0px",
    },
    "& .MuiAccordionDetails-root": {
      padding: theme.spacing(1),
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(0.8),
      },
    },
  },
  accordionHeader: {
    padding: theme.spacing(0.5, 1),
    backgroundColor: "rgba(4, 207, 102, 0.03)",
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  expandIcon: {
    color: "#04cf66",
  },
  dubaiChipIcon: {
    color: "#FF9800",
  },
  whatsappChipIcon: {
    color: "#25D366",
  },
  aiChipIcon: {
    color: "#2196F3",
  },
  businessChipIcon: {
    color: "#FF9800",
  },
  discountInfo: {
    color: "#FF9800",
    fontSize: "0.85rem",
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    backgroundColor: "rgba(255, 152, 0, 0.04)",
    borderRadius: "6px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    [theme.breakpoints.down("xs")]: {
      minHeight: "50vh",
    },
  },
  loadingText: {
    marginTop: theme.spacing(2),
    color: "#6B7280",
    fontSize: "0.9rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8rem",
    },
  },
  counterContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "20px 0",
  },
  counterButton: {
    minWidth: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#04cf66",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    border: "none",
    fontSize: "20px",
    "&:hover": {
      backgroundColor: "#03b85a",
    },
  },
  counterDisplay: {
    margin: "0 20px",
    fontSize: "24px",
    fontWeight: "bold",
    minWidth: "50px",
    textAlign: "center",
  },
}));

// Add SVG icons similar to PaymentPage
const MapPinIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const MessageSquareIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
);

const GlobeIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" x2="22" y1="12" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const BriefcaseIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect width="20" height="14" x="2" y="7" rx="2" ry="2"></rect>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
  </svg>
);

function ThankYouPage() {
  const classes = useStyles();
  const theme = useTheme();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [passes, setPasses] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [noIntentId, setNoIntentId] = useState(false);
  const [showAttendeeCounter, setShowAttendeeCounter] = useState(false);
  const [numberOfAttendees, setNumberOfAttendees] = useState(1);

  // For form errors
  const [formErrors, setFormErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  // Add industry options
  const industries = [
    "Real Estate",
    "Technology",
    "Finance",
    "Healthcare",
    "Education",
    "E-commerce",
    "Manufacturing",
    "Professional Services",
    "Marketing",
    "Media",
    "Telecommunications",
    "Travel",
    "Government & Public Sector",
    "Non-Profit",
    "Others",
  ];

  // Add organization size options
  const organizationSizes = [
    "1-10 employees",
    "10-20 employees",
    "20-50 employees",
    "50-200 employees",
    "200+ employees",
  ];

  // Function to handle attendee counter
  const handleAttendeeCountChange = (change) => {
    setNumberOfAttendees((prev) => {
      const newCount = prev + change;
      return newCount < 1 ? 1 : newCount;
    });
  };

  // Function to initialize attendees for x_attendees_form
  const initializeAttendeesForCounter = () => {
    const newAttendees = [];
    for (let i = 0; i < numberOfAttendees; i++) {
      newAttendees.push({
        passType: "Silver Pass - Single Member",
        passId: "single",
        fullName: "",
        email: "",
        countryCode: "+971",
        phone: "",
        organization: "",
        organizationSize: "",
        industry: "",
        designation: "",
        website: "",
      });
    }
    setAttendees(newAttendees);
  };

  // Function to fetch payment details
  const fetchPaymentDetails = (intentId) => {
    setLoading(true);

    axios
      .get(
        `${INTEGRATION_SERVER_URL}/xpay/t1/get-customer?paymentIntentId=${intentId}`
      )
      .then((response) => {
        const data = response.data;
        if (data.success && data.data) {
          const customerData = data.data;
          setPaymentInfo(customerData);
          setPasses(customerData.passes);

          if (customerData.members && customerData.members.length > 0) {
            setSubmitSuccess(true);
            setAttendees(
              customerData.members.map((member) => ({
                ...member,
                countryCode: member.phone?.substring(0, 4) || "+971",
                phone: member.phone?.substring(4) || "",
              }))
            );
          } else {
            // Initialize attendees based on passes
            const initialAttendees = [];

            customerData.passes.forEach((pass) => {
              const quantity = parseInt(pass.quantity, 10);
              for (let i = 0; i < quantity; i++) {
                const attendeeIndex = initialAttendees.length;
                const isFirstAttendee = attendeeIndex === 0;

                initialAttendees.push({
                  passType: pass.title,
                  passId: pass.id,
                  fullName: isFirstAttendee ? customerData.fullName || "" : "",
                  email: isFirstAttendee ? customerData.email || "" : "",
                  countryCode:
                    isFirstAttendee && customerData.phone
                      ? customerData.phone.substring(0, 4)
                      : "+971",
                  phone:
                    isFirstAttendee && customerData.phone
                      ? customerData.phone.substring(4)
                      : "",
                  organization: isFirstAttendee
                    ? customerData.organizationName || ""
                    : "",
                  organizationSize: isFirstAttendee
                    ? customerData.organizationSize || ""
                    : "",
                  industry: isFirstAttendee ? customerData.industry || "" : "",
                });
              }
            });

            setAttendees(initialAttendees);
          }
        } else {
          setNoIntentId(true);
        }
      })
      .catch((err) => {
        console.error("Error fetching payment details:", err);
        setNoIntentId(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Calculate total amount
  const calculateTotal = () => {
    return passes.reduce(
      (total, pass) => total + pass.price * pass.quantity,
      0
    );
  };

  // Update handleAttendeeChange to include new fields
  const handleAttendeeChange = (index, field, value) => {
    setAttendees((prevAttendees) => {
      const newAttendees = [...prevAttendees];
      if (!newAttendees[index]) {
        newAttendees[index] = {};
      }
      newAttendees[index][field] = value;
      return newAttendees;
    });

    // Clear any related error
    if (formErrors[`attendee-${index}-${field}`]) {
      setFormErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[`attendee-${index}-${field}`];
        return newErrors;
      });
    }
  };

  // Copy details from the first attendee
  const copyFromFirstAttendee = (index) => {
    if (attendees.length > 0 && index > 0) {
      setAttendees((prevAttendees) => {
        const newAttendees = [...prevAttendees];
        const firstAttendee = prevAttendees[0];

        newAttendees[index] = {
          ...newAttendees[index],
          fullName: firstAttendee.fullName,
          countryCode: firstAttendee.countryCode,
          phone: firstAttendee.phone,
          organization: firstAttendee.organization,
          website: firstAttendee.website,
          industry: firstAttendee.industry,
          organizationSize: firstAttendee.organizationSize,
          designation: firstAttendee.designation,
          // Keep the pass information
          passType: newAttendees[index].passType,
          passId: newAttendees[index].passId,
        };

        return newAttendees;
      });

      // Clear any errors related to this attendee
      setFormErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith(`attendee-${index}-`)) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }
  };

  // Update validateForm to include new fields
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    attendees.forEach((attendee, index) => {
      // Existing validations...
      if (!attendee.fullName || attendee.fullName.trim() === "") {
        newErrors[`attendee-${index}-fullName`] = "Full name is required";
        isValid = false;
      }

      if (!attendee.email || attendee.email.trim() === "") {
        newErrors[`attendee-${index}-email`] = "Email is required";
        isValid = false;
      } else if (!/\S+@\S+\.\S+/.test(attendee.email)) {
        newErrors[`attendee-${index}-email`] = "Invalid email format";
        isValid = false;
      }

      if (!attendee.phone || attendee.phone.trim() === "") {
        newErrors[`attendee-${index}-phone`] = "Phone number is required";
        isValid = false;
      }

      if (!attendee.organization || attendee.organization.trim() === "") {
        newErrors[`attendee-${index}-organization`] =
          "Organization is required";
        isValid = false;
      }

      // Only validate these fields if not in x_attendees_form mode
      if (!showAttendeeCounter) {
        if (!attendee.organizationSize) {
          newErrors[`attendee-${index}-organizationSize`] =
            "Organization size is required";
          isValid = false;
        }

        if (!attendee.industry) {
          newErrors[`attendee-${index}-industry`] = "Industry is required";
          isValid = false;
        }

        if (!attendee.designation) {
          newErrors[`attendee-${index}-designation`] = "Designation is required";
          isValid = false;
        }
      }
    });

    setFormErrors(newErrors);
    return isValid;
  };

  // Update handleSubmit to include new fields in API call
  const handleSubmit = async () => {
    if (!validateForm()) {
      const firstErrorField = document.querySelector('[aria-invalid="true"]');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setIsProcessing(true);

    try {
      const searchParams = new URLSearchParams(location.search);
      const intentId = searchParams.get("xpay_intent_id");

      if (!showAttendeeCounter && !intentId) {
        throw new Error("Payment intent ID not found");
      }

      const members = attendees.map((attendee) => ({
        fullName: attendee.fullName,
        email: attendee.email,
        phone: `${attendee.countryCode}${attendee.phone}`,
        organization: attendee.organization,
        organizationSize: attendee.organizationSize,
        industry: attendee.industry,
        designation: attendee.designation,
        website: attendee.website || "",
        passId: attendee.passId,
        passType: attendee.passType,
      }));

      const response = await axios.post(
        `${INTEGRATION_SERVER_URL}/xpay/t1/update-members`,
        {
          intentId,
          members,
          isAttendeeForm: showAttendeeCounter ? true : false,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setSubmitSuccess(true);
        window.scrollTo(0, 0);
      } else {
        throw new Error(
          response.data.message || "Failed to update member information"
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setFormErrors((prev) => ({
        ...prev,
        form:
          error.response?.data?.message ||
          error.message ||
          "An error occurred while submitting the form. Please try again.",
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  // Effect to check for x_attendees_form parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const intentId = searchParams.get("xpay_intent_id");
    const showAttendeeForm = searchParams.get("x_attendees_form");

    if (showAttendeeForm) {
      console.log("Showing attendee counter");
      setShowAttendeeCounter(true);
      setLoading(false);
    } else if (intentId) {
      fetchPaymentDetails(intentId);
    } else {
      setLoading(false);
      setNoIntentId(true);
    }
  }, [location]);

  // Effect to update attendees when counter changes
  useEffect(() => {
    if (showAttendeeCounter) {
      initializeAttendeesForCounter();
    }
  }, [numberOfAttendees, showAttendeeCounter]);

  // Add meta tag to prevent auto-zoom on iOS
  useEffect(() => {
    const metaTag = document.createElement("meta");
    metaTag.name = "viewport";
    metaTag.content =
      "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no";
    document.getElementsByTagName("head")[0].appendChild(metaTag);

    return () => {
      document.getElementsByTagName("head")[0].removeChild(metaTag);
    };
  }, []);

  // Update document title and set up mutation observer
  useEffect(() => {
    // Set document title
    document.title = "Thank You - EVOLVE'25 Dubai";

    // Create a mutation observer to monitor title changes
    const titleObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "childList" &&
          document.title !== "Thank You - EVOLVE'25 Dubai"
        ) {
          document.title = "Thank You - EVOLVE'25 Dubai";
        }
      });
    });

    // Start observing the document title
    titleObserver.observe(document.querySelector("title"), {
      childList: true,
    });

    // Inject Plausible Analytics
    const plausibleScript = document.createElement("script");
    plausibleScript.defer = true;
    plausibleScript.setAttribute("data-domain", "checkout.aisensy.com");
    plausibleScript.src = "https://plausible.io/js/script.js";
    document.head.appendChild(plausibleScript);

    // Inject Pagesense
    const pagesenseScript = document.createElement("script");
    pagesenseScript.type = "text/javascript";
    pagesenseScript.innerHTML = `(function(w,s){var e=document.createElement("script");e.type="text/javascript";e.async=true;e.src="https://cdn-in.pagesense.io/js/aisensy/23640c72623a4639b23b58efd93a4209.js";var x=document.getElementsByTagName("script")[0];x.parentNode.insertBefore(e,x);})(window,"script");`;
    document.head.appendChild(pagesenseScript);

    // Inject First Meta Pixel
    const metaPixel1Script = document.createElement("script");
    metaPixel1Script.innerHTML = `!function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '255162156131712');
    fbq('track', 'PageView');`;
    document.head.appendChild(metaPixel1Script);

    const metaPixel1Noscript = document.createElement("noscript");
    const metaPixel1Img = document.createElement("img");
    metaPixel1Img.height = "1";
    metaPixel1Img.width = "1";
    metaPixel1Img.style.display = "none";
    metaPixel1Img.src =
      "https://www.facebook.com/tr?id=255162156131712&ev=PageView&noscript=1";
    metaPixel1Noscript.appendChild(metaPixel1Img);
    document.head.appendChild(metaPixel1Noscript);

    // Inject Second Meta Pixel
    const metaPixel2Script = document.createElement("script");
    metaPixel2Script.innerHTML = `!function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '890505489280353');
    fbq('track', 'PageView');`;
    document.head.appendChild(metaPixel2Script);

    const metaPixel2Noscript = document.createElement("noscript");
    const metaPixel2Img = document.createElement("img");
    metaPixel2Img.height = "1";
    metaPixel2Img.width = "1";
    metaPixel2Img.style.display = "none";
    metaPixel2Img.src =
      "https://www.facebook.com/tr?id=890505489280353&ev=PageView&noscript=1";
    metaPixel2Noscript.appendChild(metaPixel2Img);
    document.head.appendChild(metaPixel2Noscript);

    // Inject Google Tag Manager
    const gtmScript = document.createElement("script");
    gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','GTM-NRR25F8');`;
    document.head.appendChild(gtmScript);

    // Inject Google Analytics
    const gtagScript = document.createElement("script");
    gtagScript.async = true;
    gtagScript.src = "https://www.googletagmanager.com/gtag/js?id=G-B7BMELF36T";
    document.head.appendChild(gtagScript);

    const gtagConfigScript = document.createElement("script");
    gtagConfigScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-B7BMELF36T');
    `;
    document.head.appendChild(gtagConfigScript);

    return () => {
      // Disconnect the observer when component unmounts
      titleObserver.disconnect();
      // Clean up scripts when component unmounts
      document.head.removeChild(plausibleScript);
      document.head.removeChild(pagesenseScript);
      document.head.removeChild(metaPixel1Script);
      document.head.removeChild(metaPixel1Noscript);
      document.head.removeChild(metaPixel2Script);
      document.head.removeChild(metaPixel2Noscript);
      document.head.removeChild(gtmScript);
      document.head.removeChild(gtagScript);
      document.head.removeChild(gtagConfigScript);
    };
  }, []);

  // Add accordion toggle function
  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  // Show loading spinner while fetching data
  if (loading) {
    return (
      <div className={classes.root}>
        <Container className={classes.container} maxWidth="lg">
          <Box className={classes.loadingContainer}>
            <CircularProgress style={{ color: "#04cf66" }} />
            <Typography className={classes.loadingText}>
              Loading your registration details...
            </Typography>
          </Box>
        </Container>
      </div>
    );
  }

  // Show error message if no intent ID or error fetching data
  if (noIntentId && !showAttendeeCounter) {
    return (
      <div className={classes.root}>
        <Container className={classes.container} maxWidth="lg">
          <Box className={classes.contentWrapper}>
            <Box className={classes.header}>
              <Box className={classes.logoContainer}>
                <img
                  src={AiSensyLogo}
                  alt="AiSensy Logo"
                  className={classes.logo}
                />
              </Box>
            </Box>
            <Typography className={classes.evolveTitle}>EVOLVE'25</Typography>
            <Typography className={classes.subtitle}>
              LARGEST WHATSAPP BUSINESS EVENT
            </Typography>
            <Box className={classes.thankYouSection}>
              <Typography
                variant="h5"
                style={{ color: "#04cf66", fontWeight: 600 }}
              >
                Your registration for Evolve: Dubai AI Edition is complete
              </Typography>
              <Typography variant="body1" style={{ marginTop: "1rem" }}>
                You'll receive your passes shortly
              </Typography>
              <Button
                component={Link}
                to="/evolve"
                variant="contained"
                style={{
                  background: "linear-gradient(to right, #04cf66, #28E0B9)",
                  color: "white",
                  marginTop: "1.5rem",
                  fontWeight: 600,
                  padding: "0.7rem 2rem",
                  borderRadius: "8px",
                  [theme.breakpoints.down("xs")]: {
                    padding: "0.6rem 1.5rem",
                    fontSize: "0.9rem",
                  },
                }}
              >
                Book Another Pass
              </Button>
            </Box>
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Container className={classes.container} maxWidth="lg">
        <Box className={classes.contentWrapper}>
          <Box className={classes.header}>
            <Box className={classes.logoContainer}>
              <img
                src={AiSensyLogo}
                alt="AiSensy Logo"
                className={classes.logo}
              />
            </Box>
          </Box>

          <Typography className={classes.evolveTitle}>EVOLVE'25</Typography>
          <Typography className={classes.subtitle}>
            LARGEST WHATSAPP BUSINESS EVENT
          </Typography>

          {/* Event badges/chips */}
          <Box className={classes.eventBadges}>
            <Chip
              icon={
                <MapPinIcon
                  className={`${classes.customIcon} ${classes.dubaiChipIcon}`}
                  style={{ marginRight: "-4px" }}
                />
              }
              label="Dubai"
              className={classes.dubaiChip}
              size="small"
            />
            <Chip
              icon={
                <MessageSquareIcon
                  className={`${classes.customIcon} ${classes.whatsappChipIcon}`}
                  style={{ marginRight: "-4px" }}
                />
              }
              label="WhatsApp"
              className={classes.whatsappChip}
              size="small"
            />
            <Chip
              icon={
                <GlobeIcon
                  className={`${classes.customIcon} ${classes.aiChipIcon}`}
                  style={{ marginRight: "-4px" }}
                />
              }
              label="AI"
              className={classes.aiChip}
              size="small"
            />
            <Chip
              icon={
                <BriefcaseIcon
                  className={`${classes.customIcon} ${classes.businessChipIcon}`}
                  style={{ marginRight: "-4px" }}
                />
              }
              label="Business"
              className={classes.businessChip}
              size="small"
            />
          </Box>

          {/* Time and location near the heading */}
          <Box className={classes.eventTimeLocation}>
            <Box className={classes.eventDetailPill}>
              <EventIcon className={classes.eventDetailIcon} />
              <span>April 25th, 2025 | 9:00 AM - 6:00 PM</span>
            </Box>
            <Box className={classes.eventDetailPill}>
              <PlaceIcon className={classes.eventDetailIcon} />
              <span>Millennium Plaza Downtown Hotel,Dubai</span>
            </Box>
          </Box>

          {showAttendeeCounter ? (
            <>
              {submitSuccess ? (
                <Box className={classes.thankYouSection}>
                  <Box className={classes.successIcon}>
                    <CheckCircleIcon />
                  </Box>
                  <Typography className={classes.congratsText}>
                    Registration Complete!
                  </Typography>
                  <Typography variant="body1" className={classes.messageText}>
                    You're all set for{" "}
                    <span className={classes.highlightText}>
                      EVOLVE'25 Dubai
                    </span>
                    ! Your tickets and event details have been sent to your
                    email.
                  </Typography>
                  <Button
                    component={Link}
                    to="/evolve"
                    variant="contained"
                    className={classes.bookAgainButton}
                  >
                    Book Another Pass
                  </Button>
                </Box>
              ) : (
                <>
                  <Box className={classes.thankYouSection}>
                    <Typography className={classes.congratsText}>
                      Step In. Stand Out. Welcome to the EVOLVE'25!
                    </Typography>
                    <Typography variant="body1" className={classes.messageText}>
                      Time to rally the squad! How many spots should we save for
                      you?
                    </Typography>

                    <Box className={classes.counterContainer}>
                      <button
                        className={classes.counterButton}
                        onClick={() => handleAttendeeCountChange(-1)}
                      >
                        -
                      </button>
                      <Typography className={classes.counterDisplay}>
                        {numberOfAttendees}
                      </Typography>
                      <button
                        className={classes.counterButton}
                        onClick={() => handleAttendeeCountChange(1)}
                      >
                        +
                      </button>
                    </Box>
                  </Box>

                  {attendees.length > 0 && (
                    <Box className={classes.formSection}>
                      <Typography className={classes.formTitle}>
                        Complete Attendee Information
                      </Typography>

                      {attendees.map((attendee, index) => (
                        <Box key={index} className={classes.attendeeForm}>
                          <Box className={classes.formHeader}>
                            <Typography className={classes.attendeeTitle}>
                              <span className={classes.attendeeCount}>
                                {index + 1}
                              </span>
                              {attendee.passType}
                            </Typography>

                            {index > 0 && (
                              <Button
                                startIcon={
                                  <ContentCopyIcon
                                    className={classes.copyIcon}
                                  />
                                }
                                size="small"
                                onClick={() => copyFromFirstAttendee(index)}
                                className={classes.copyButton}
                              >
                                Same as First
                              </Button>
                            )}
                          </Box>

                          <Box className={classes.formContent}>
                            <Grid
                              container
                              spacing={2}
                              {...(theme.breakpoints.down("xs")
                                ? { spacing: 1 }
                                : {})}
                            >
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  className={classes.formField}
                                  label="Full Name"
                                  variant="outlined"
                                  fullWidth
                                  value={attendee.fullName || ""}
                                  onChange={(e) =>
                                    handleAttendeeChange(
                                      index,
                                      "fullName",
                                      e.target.value
                                    )
                                  }
                                  error={
                                    !!formErrors[`attendee-${index}-fullName`]
                                  }
                                  helperText={
                                    formErrors[`attendee-${index}-fullName`] ||
                                    ""
                                  }
                                  required
                                  InputProps={{
                                    classes: {
                                      root: classes.inputRoot,
                                      focused: classes.inputFocused,
                                    },
                                  }}
                                  size="small"
                                />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField
                                  className={classes.formField}
                                  label="Email Address"
                                  variant="outlined"
                                  type="email"
                                  fullWidth
                                  value={attendee.email || ""}
                                  onChange={(e) =>
                                    handleAttendeeChange(
                                      index,
                                      "email",
                                      e.target.value
                                    )
                                  }
                                  error={
                                    !!formErrors[`attendee-${index}-email`]
                                  }
                                  helperText={
                                    formErrors[`attendee-${index}-email`] || ""
                                  }
                                  required
                                  InputProps={{
                                    classes: {
                                      root: classes.inputRoot,
                                      focused: classes.inputFocused,
                                    },
                                  }}
                                  size="small"
                                />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <Box className={classes.phoneInput}>
                                  <FormControl
                                    variant="outlined"
                                    className={classes.countrySelect}
                                    size="small"
                                  >
                                    {/* <InputLabel>Code</InputLabel> */}
                                    <Select
                                      value={attendee.countryCode || "+971"}
                                      onChange={(e) =>
                                        handleAttendeeChange(
                                          index,
                                          "countryCode",
                                          e.target.value
                                        )
                                      }
                                      label="Code"
                                      renderValue={(value) => {
                                        const country = countries.find(c => c.dial_code === value);
                                        return country ? country.dial_code : value;
                                      }}
                                    >
                                      {countries.map((country) => (
                                        <MenuItem
                                          key={country.code}
                                          value={country.dial_code}
                                        >
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                          >
                                            <span style={{ marginRight: 8 }}>
                                              {country.flag}
                                            </span>
                                            <span style={{ marginRight: 8 }}>
                                              {country.dial_code}
                                            </span>
                                            <span>{country.name}</span>
                                          </Box>
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>

                                  <TextField
                                    className={classes.formField}
                                    label="Phone Number"
                                    variant="outlined"
                                    fullWidth
                                    value={attendee.phone || ""}
                                    onChange={(e) =>
                                      handleAttendeeChange(
                                        index,
                                        "phone",
                                        e.target.value
                                      )
                                    }
                                    error={
                                      !!formErrors[`attendee-${index}-phone`]
                                    }
                                    helperText={
                                      formErrors[`attendee-${index}-phone`] ||
                                      ""
                                    }
                                    required
                                    InputProps={{
                                      classes: {
                                        root: classes.inputRoot,
                                        focused: classes.inputFocused,
                                      },
                                    }}
                                    size="small"
                                  />
                                </Box>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField
                                  className={classes.formField}
                                  label="Organization"
                                  variant="outlined"
                                  fullWidth
                                  value={attendee.organization || ""}
                                  onChange={(e) =>
                                    handleAttendeeChange(
                                      index,
                                      "organization",
                                      e.target.value
                                    )
                                  }
                                  error={
                                    !!formErrors[
                                      `attendee-${index}-organization`
                                    ]
                                  }
                                  helperText={
                                    formErrors[
                                      `attendee-${index}-organization`
                                    ] || ""
                                  }
                                  required
                                  InputProps={{
                                    classes: {
                                      root: classes.inputRoot,
                                      focused: classes.inputFocused,
                                    },
                                  }}
                                  size="small"
                                />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField
                                  className={classes.formField}
                                  label="Designation"
                                  variant="outlined"
                                  fullWidth
                                  value={attendee.designation || ""}
                                  onChange={(e) =>
                                    handleAttendeeChange(
                                      index,
                                      "designation",
                                      e.target.value
                                    )
                                  }
                                  error={
                                    !!formErrors[
                                      `attendee-${index}-designation`
                                    ]
                                  }
                                  helperText={
                                    formErrors[
                                      `attendee-${index}-designation`
                                    ] || ""
                                  }
                                  required
                                  InputProps={{
                                    classes: {
                                      root: classes.inputRoot,
                                      focused: classes.inputFocused,
                                    },
                                  }}
                                  size="small"
                                />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField
                                  className={classes.formField}
                                  label="Website"
                                  variant="outlined"
                                  fullWidth
                                  value={attendee.website || ""}
                                  onChange={(e) =>
                                    handleAttendeeChange(
                                      index,
                                      "website",
                                      e.target.value
                                    )
                                  }
                                  error={
                                    !!formErrors[`attendee-${index}-website`]
                                  }
                                  helperText={
                                    formErrors[`attendee-${index}-website`] ||
                                    ""
                                  }
                                  InputProps={{
                                    classes: {
                                      root: classes.inputRoot,
                                      focused: classes.inputFocused,
                                    },
                                  }}
                                  size="small"
                                />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <FormControl
                                  variant="outlined"
                                  className={classes.formField}
                                  fullWidth
                                  error={
                                    !!formErrors[`attendee-${index}-industry`]
                                  }
                                  required
                                  size="small"
                                >
                                  <InputLabel>Industry</InputLabel>
                                  <Select
                                    value={attendee.industry || ""}
                                    onChange={(e) =>
                                      handleAttendeeChange(
                                        index,
                                        "industry",
                                        e.target.value
                                      )
                                    }
                                    label="Industry"
                                    style={{ backgroundColor: "#f0f0f0" }}
                                  >
                                    {industries.map((industry) => (
                                      <MenuItem
                                        key={industry}
                                        value={industry}
                                        style={{ fontSize: "0.9rem" }}
                                      >
                                        {industry}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {formErrors[`attendee-${index}-industry`] && (
                                    <FormHelperText>
                                      {formErrors[`attendee-${index}-industry`]}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <FormControl
                                  variant="outlined"
                                  className={classes.formField}
                                  fullWidth
                                  error={
                                    !!formErrors[
                                      `attendee-${index}-organizationSize`
                                    ]
                                  }
                                  required
                                  size="small"
                                >
                                  <InputLabel>Organization Size</InputLabel>
                                  <Select
                                    value={attendee.organizationSize || ""}
                                    onChange={(e) =>
                                      handleAttendeeChange(
                                        index,
                                        "organizationSize",
                                        e.target.value
                                      )
                                    }
                                    label="Organization Size"
                                    style={{ backgroundColor: "#f0f0f0" }}
                                  >
                                    {organizationSizes.map((size) => (
                                      <MenuItem
                                        key={size}
                                        value={size}
                                        style={{ fontSize: "0.9rem" }}
                                      >
                                        {size}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {formErrors[
                                    `attendee-${index}-organizationSize`
                                  ] && (
                                    <FormHelperText>
                                      {
                                        formErrors[
                                          `attendee-${index}-organizationSize`
                                        ]
                                      }
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </Grid>
                            </Grid>
                          </Box>
                        </Box>
                      ))}

                      <Box className={classes.formSubmitArea}>
                        <Button
                          color="primary"
                          size="large"
                          disabled={isProcessing}
                          onClick={handleSubmit}
                          className={classes.submitButton}
                        >
                          {isProcessing ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            "Submit"
                          )}
                        </Button>

                        {formErrors.form && (
                          <Typography
                            style={{
                              color: "#f44336",
                              marginTop: "10px",
                              fontSize: "0.8rem",
                              textAlign: "center",
                            }}
                          >
                            {formErrors.form}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <Box className={classes.thankYouSection}>
                {submitSuccess ? (
                  <>
                    <Box className={classes.successIcon}>
                      <CheckCircleIcon />
                    </Box>
                    <Typography className={classes.congratsText}>
                      Registration Complete!
                    </Typography>
                    <Typography variant="body1" className={classes.messageText}>
                      You're all set for{" "}
                      <span className={classes.highlightText}>
                        EVOLVE'25 Dubai
                      </span>
                      ! Your tickets and event details have been sent to your
                      email.
                    </Typography>
                    <Button
                      component={Link}
                      to="/evolve"
                      variant="contained"
                      className={classes.bookAgainButton}
                    >
                      Book Another Pass
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography className={classes.congratsText}>
                      Thank You for Your Purchase!
                    </Typography>
                    <Typography variant="body1" className={classes.messageText}>
                      You're almost there! Your tickets for{" "}
                      <span className={classes.highlightText}>
                        EVOLVE'25 Dubai
                      </span>{" "}
                      are reserved. Just complete the attendee details below to
                      lock in your spot—make sure everything's accurate so your
                      passes land safely in your inbox!
                    </Typography>
                  </>
                )}
              </Box>

              {!submitSuccess && (
                <>
                  <Box className={classes.orderSummarySection}>
                    <Typography className={classes.sectionTitle}>
                      Order Summary
                    </Typography>

                    <Accordion
                      className={classes.summaryAccordion}
                      expanded={expanded}
                      onChange={handleAccordionChange}
                      defaultExpanded
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon className={classes.expandIcon} />
                        }
                      >
                        <Box className={classes.accordionHeader}>
                          <EventIcon className={classes.ticketHeaderIcon} />
                          <Typography className={classes.ticketTitle}>
                            Purchased Tickets
                          </Typography>
                        </Box>
                      </AccordionSummary>

                      <AccordionDetails>
                        <Box
                          className={classes.ticketContent}
                          style={{ width: "100%" }}
                        >
                          {passes.map((pass, index) => (
                            <Box key={index} className={classes.ticketDetail}>
                              <Typography className={classes.ticketType}>
                                {pass.title} x {pass.quantity}
                              </Typography>
                              <Typography className={classes.ticketPrice}>
                                AED {pass.price * pass.quantity}
                              </Typography>
                            </Box>
                          ))}

                          <Divider className={classes.divider} />

                          {paymentInfo?.discount > 0 && (
                            <Box className={classes.discountInfo}>
                              <Typography>Discount Applied</Typography>
                              <Typography>
                                - AED {paymentInfo.discount}
                              </Typography>
                            </Box>
                          )}

                          <Box className={classes.ticketTotal}>
                            <Typography className={classes.totalText}>
                              Total Amount Paid
                            </Typography>
                            <Typography className={classes.totalAmount}>
                              AED {paymentInfo?.finalAmount || calculateTotal()}
                            </Typography>
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Box>

                  <Box className={classes.formSection}>
                    <Typography className={classes.formTitle}>
                      Complete Attendee Information
                    </Typography>

                    {attendees.map((attendee, index) => (
                      <Box key={index} className={classes.attendeeForm}>
                        <Box className={classes.formHeader}>
                          <Typography className={classes.attendeeTitle}>
                            <span className={classes.attendeeCount}>
                              {index + 1}
                            </span>
                            {attendee.passType}
                          </Typography>

                          {index > 0 && (
                            <Button
                              startIcon={
                                <ContentCopyIcon className={classes.copyIcon} />
                              }
                              size="small"
                              onClick={() => copyFromFirstAttendee(index)}
                              className={classes.copyButton}
                            >
                              Same as First
                            </Button>
                          )}
                        </Box>

                        <Box className={classes.formContent}>
                          <Grid
                            container
                            spacing={2}
                            {...(theme.breakpoints.down("xs")
                              ? { spacing: 1 }
                              : {})}
                          >
                            <Grid item xs={12} sm={6}>
                              <TextField
                                className={classes.formField}
                                label="Full Name"
                                variant="outlined"
                                fullWidth
                                value={attendee.fullName || ""}
                                onChange={(e) =>
                                  handleAttendeeChange(
                                    index,
                                    "fullName",
                                    e.target.value
                                  )
                                }
                                error={
                                  !!formErrors[`attendee-${index}-fullName`]
                                }
                                helperText={
                                  formErrors[`attendee-${index}-fullName`] || ""
                                }
                                required
                                InputProps={{
                                  classes: {
                                    root: classes.inputRoot,
                                    focused: classes.inputFocused,
                                  },
                                }}
                                size="small"
                              />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <TextField
                                className={classes.formField}
                                label="Email Address"
                                variant="outlined"
                                type="email"
                                fullWidth
                                value={attendee.email || ""}
                                onChange={(e) =>
                                  handleAttendeeChange(
                                    index,
                                    "email",
                                    e.target.value
                                  )
                                }
                                error={!!formErrors[`attendee-${index}-email`]}
                                helperText={
                                  formErrors[`attendee-${index}-email`] || ""
                                }
                                required
                                InputProps={{
                                  classes: {
                                    root: classes.inputRoot,
                                    focused: classes.inputFocused,
                                  },
                                }}
                                size="small"
                              />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Box className={classes.phoneInput}>
                                <FormControl
                                  variant="outlined"
                                  className={classes.countrySelect}
                                  size="small"
                                >
                                  <InputLabel>Code</InputLabel>
                                  <Select
                                    value={attendee.countryCode || "+971"}
                                    onChange={(e) =>
                                      handleAttendeeChange(
                                        index,
                                        "countryCode",
                                        e.target.value
                                      )
                                    }
                                    label="Code"
                                    renderValue={(value) => {
                                      const country = countries.find(c => c.dial_code === value);
                                      return country ? country.dial_code : value;
                                    }}
                                  >
                                    {countries.map((country) => (
                                      <MenuItem
                                        key={country.code}
                                        value={country.dial_code}
                                      >
                                        <Box display="flex" alignItems="center">
                                          <span style={{ marginRight: 8 }}>
                                            {country.flag}
                                          </span>
                                          <span style={{ marginRight: 8 }}>
                                            {country.dial_code}
                                          </span>
                                          <span>{country.name}</span>
                                        </Box>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>

                                <TextField
                                  className={classes.formField}
                                  label="Phone Number"
                                  variant="outlined"
                                  fullWidth
                                  value={attendee.phone || ""}
                                  onChange={(e) =>
                                    handleAttendeeChange(
                                      index,
                                      "phone",
                                      e.target.value
                                    )
                                  }
                                  error={
                                    !!formErrors[`attendee-${index}-phone`]
                                  }
                                  helperText={
                                    formErrors[`attendee-${index}-phone`] || ""
                                  }
                                  required
                                  InputProps={{
                                    classes: {
                                      root: classes.inputRoot,
                                      focused: classes.inputFocused,
                                    },
                                  }}
                                  size="small"
                                />
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <TextField
                                className={classes.formField}
                                label="Organization"
                                variant="outlined"
                                fullWidth
                                value={attendee.organization || ""}
                                onChange={(e) =>
                                  handleAttendeeChange(
                                    index,
                                    "organization",
                                    e.target.value
                                  )
                                }
                                error={
                                  !!formErrors[`attendee-${index}-organization`]
                                }
                                helperText={
                                  formErrors[
                                    `attendee-${index}-organization`
                                  ] || ""
                                }
                                required
                                InputProps={{
                                  classes: {
                                    root: classes.inputRoot,
                                    focused: classes.inputFocused,
                                  },
                                }}
                                size="small"
                              />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <TextField
                                className={classes.formField}
                                label="Designation"
                                variant="outlined"
                                fullWidth
                                value={attendee.designation || ""}
                                onChange={(e) =>
                                  handleAttendeeChange(
                                    index,
                                    "designation",
                                    e.target.value
                                  )
                                }
                                error={
                                  !!formErrors[`attendee-${index}-designation`]
                                }
                                helperText={
                                  formErrors[`attendee-${index}-designation`] ||
                                  ""
                                }
                                required
                                InputProps={{
                                  classes: {
                                    root: classes.inputRoot,
                                    focused: classes.inputFocused,
                                  },
                                }}
                                size="small"
                              />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <TextField
                                className={classes.formField}
                                label="Website"
                                variant="outlined"
                                fullWidth
                                value={attendee.website || ""}
                                onChange={(e) =>
                                  handleAttendeeChange(
                                    index,
                                    "website",
                                    e.target.value
                                  )
                                }
                                error={
                                  !!formErrors[`attendee-${index}-website`]
                                }
                                helperText={
                                  formErrors[`attendee-${index}-website`] || ""
                                }
                                InputProps={{
                                  classes: {
                                    root: classes.inputRoot,
                                    focused: classes.inputFocused,
                                  },
                                }}
                                size="small"
                              />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <FormControl
                                variant="outlined"
                                className={classes.formField}
                                fullWidth
                                error={
                                  !!formErrors[`attendee-${index}-industry`]
                                }
                                required
                                size="small"
                              >
                                <InputLabel>Industry</InputLabel>
                                <Select
                                  value={attendee.industry || ""}
                                  onChange={(e) =>
                                    handleAttendeeChange(
                                      index,
                                      "industry",
                                      e.target.value
                                    )
                                  }
                                  label="Industry"
                                  style={{ backgroundColor: "#f0f0f0" }}
                                >
                                  {industries.map((industry) => (
                                    <MenuItem
                                      key={industry}
                                      value={industry}
                                      style={{ fontSize: "0.9rem" }}
                                    >
                                      {industry}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {formErrors[`attendee-${index}-industry`] && (
                                  <FormHelperText>
                                    {formErrors[`attendee-${index}-industry`]}
                                  </FormHelperText>
                                )}
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <FormControl
                                variant="outlined"
                                className={classes.formField}
                                fullWidth
                                error={
                                  !!formErrors[
                                    `attendee-${index}-organizationSize`
                                  ]
                                }
                                required
                                size="small"
                              >
                                <InputLabel>Organization Size</InputLabel>
                                <Select
                                  value={attendee.organizationSize || ""}
                                  onChange={(e) =>
                                    handleAttendeeChange(
                                      index,
                                      "organizationSize",
                                      e.target.value
                                    )
                                  }
                                  label="Organization Size"
                                  style={{ backgroundColor: "#f0f0f0" }}
                                >
                                  {organizationSizes.map((size) => (
                                    <MenuItem
                                      key={size}
                                      value={size}
                                      style={{ fontSize: "0.9rem" }}
                                    >
                                      {size}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {formErrors[
                                  `attendee-${index}-organizationSize`
                                ] && (
                                  <FormHelperText>
                                    {
                                      formErrors[
                                        `attendee-${index}-organizationSize`
                                      ]
                                    }
                                  </FormHelperText>
                                )}
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                    ))}

                    <Box className={classes.formSubmitArea}>
                      <Button
                        color="primary"
                        size="large"
                        disabled={isProcessing}
                        onClick={handleSubmit}
                        className={classes.submitButton}
                      >
                        {isProcessing ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Submit"
                        )}
                      </Button>

                      {formErrors.form && (
                        <Typography
                          style={{
                            color: "#f44336",
                            marginTop: "10px",
                            fontSize: "0.8rem",
                            textAlign: "center",
                          }}
                        >
                          {formErrors.form}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </Container>
    </div>
  );
}

export default ThankYouPage;
